// Admission marks spreadsheet — test papers, interview rubrics and
// additional criteria for many applicants at once.
//
//   buildMarksTemplate — one row per applicant awaiting marks, already
//     carrying any marks entered so far, one column per paper / rubric
//     criterion / criterion of THIS cycle. Headers match the applications
//     export, so an exported file can be filled in and uploaded back.
//   parseMarksSheet    — read the filled sheet back, match rows by
//     application number and
//     check every value against the cycle the way the server will. Only
//     what actually changed is sent; blank cells leave marks as they are.
//
// Nothing here hits the network — callers pass the cycle and its
// applications. The server re-checks everything on save.

import { readFirstSheetAsRows, writeSheet, type XlsxColumnSpec } from './xlsx';
import type { AdmissionApplication, AdmissionCycle, ApplicationStatus, AssessmentPathway, InterviewRecommendation } from '../types/admissions';
import { assessmentPlan, isScored, markingBlockers, onStep, pathwayFor, ASSESSMENT_MARKING, SCORE_EDITABLE, type AssessmentPlan } from './admissionAssessment';
import { classNameOf, marksHeader } from './admissionRecords';
import { RECOMMENDATION_LABEL } from '../components/admissions/dialogs/InterviewRoundDialog';
import { statusLabel } from '../components/common/StatusChip';
import { fmtClassName } from './pdf/format';

/** One applicant's marks as the bulk endpoint takes them. */
export interface BulkMarksPayloadRow {
  applicationId: string;
  testScores?: Array<{ paperKey: string; obtained: number; absent: boolean }>;
  interviewRounds?: Array<{
    roundKey: string;
    rubricScores?: Array<{ criterion: string; obtained?: number; answer?: string }>;
    totalMarks?: number;
    recommendation?: InterviewRecommendation;
    interviewer?: string;
    comments?: string;
  }>;
  customScores?: Record<string, number>;
}

export interface ParsedMarksRow {
  rowNumber: number;
  applicationNumber: string;
  name: string;
  app?: AdmissionApplication;
  /** "English 45 → 47" — what saving this row changes. */
  changes: string[];
  errors: string[];
  state: 'ready' | 'unchanged' | 'error';
  payload?: BulkMarksPayloadRow;
}

const ABSENT = 'Absent';
const RECOMMENDATION_BY_LABEL = new Map<string, InterviewRecommendation>(
  (Object.entries(RECOMMENDATION_LABEL) as Array<[InterviewRecommendation, string]>).map(([k, l]) => [l.toLowerCase(), k]),
);

const usesAssessment = (plan: AssessmentPlan) => !!(plan.test || plan.interview);

/** Applicants whose marks can be entered now — the template's rows. */
export function markableApplications(apps: AdmissionApplication[], plan: AssessmentPlan): AdmissionApplication[] {
  const statuses = usesAssessment(plan) ? ASSESSMENT_MARKING : SCORE_EDITABLE;
  return apps
    .filter(a => statuses.has(a.status))
    .sort((x, y) => classNameOf(x).localeCompare(classNameOf(y), undefined, { numeric: true })
      || (x.admitCard?.rollNumber ?? x.applicationNumber).localeCompare(y.admitCard?.rollNumber ?? y.applicationNumber, undefined, { numeric: true }));
}

/** Applicants still on their way to marking (in review, awaiting the fee…) —
 *  they join the template once they reach the marking stage. */
export function notYetMarkable(apps: AdmissionApplication[], plan: AssessmentPlan): AdmissionApplication[] {
  const before: ApplicationStatus[] = usesAssessment(plan)
    ? ['Submitted', 'AppFeePending', 'UnderReview', 'DocsRequired']
    : ['Submitted', 'AppFeePending'];
  return apps.filter(a => before.includes(a.status));
}

// ─── Template ───────────────────────────────────────────────────────────────

/** The marks sheet for one pathway: its applicants, its papers and rounds. */
export async function buildMarksTemplate(pathway: AssessmentPathway, apps: AdmissionApplication[]): Promise<Blob> {
  const plan = assessmentPlan(pathway);
  const columns: Array<XlsxColumnSpec & { value: (a: AdmissionApplication) => string | number | null }> = [
    { header: marksHeader.applicationNumber, width: 16, value: a => a.applicationNumber },
    { header: 'Applicant', width: 26, value: a => a.name },
    { header: 'Class', width: 12, value: a => fmtClassName(classNameOf(a)) },
    ...(plan.admitCards ? [{ header: 'Roll number', width: 16, value: (a: AdmissionApplication) => a.admitCard?.rollNumber ?? '' }] : []),
    { header: 'Status', width: 20, value: a => statusLabel({ kind: 'application', value: a.status }) },
    ...(plan.test?.papers ?? []).map(p => ({
      header: marksHeader.paper(p), width: Math.max(14, p.subject.length + 9),
      // A paper also takes "Absent", so no numeric validation here.
      value: (a: AdmissionApplication) => { const s = a.testScores?.find(x => x.paperKey === p.key); return !s ? null : s.absent ? ABSENT : s.obtained; },
    })),
    ...(plan.interview?.rounds ?? []).flatMap(r => {
      const sc = (a: AdmissionApplication) => a.interviewRounds?.find(x => x.roundKey === r.key);
      return [
        // The round's questions in sheet order: marks for scored ones, the
        // answer for choice / note ones; a single total when none is scored.
        ...r.rubric.map(c => (isScored(c)
          ? {
            header: marksHeader.criterion(r, c), width: 20,
            validation: { kind: 'decimal-range' as const, min: 0, max: c.maxMarks },
            value: (a: AdmissionApplication) => sc(a)?.rubricScores?.find(x => x.criterion === c.criterion)?.obtained ?? null,
          }
          : {
            header: marksHeader.answer(r, c), width: c.kind === 'note' ? 30 : 20,
            // Excel's inline dropdown splits on commas; the parser still checks the answer.
            validation: c.kind === 'choice' && (c.options || []).every(o => !/[,"]/.test(o))
              ? { kind: 'list' as const, values: c.options || [] } : undefined,
            value: (a: AdmissionApplication) => sc(a)?.rubricScores?.find(x => x.criterion === c.criterion)?.answer ?? '',
          })),
        ...(r.rubric.some(isScored) ? [] : [{
          header: marksHeader.round(r), width: Math.max(14, r.name.length + 9),
          validation: { kind: 'decimal-range' as const, min: 0, max: r.maxMarks },
          value: (a: AdmissionApplication) => sc(a)?.totalMarks ?? null,
        }]),
        {
          header: marksHeader.recommendation(r), width: 22,
          validation: { kind: 'list' as const, values: Object.values(RECOMMENDATION_LABEL) },
          value: (a: AdmissionApplication) => { const rec = sc(a)?.recommendation; return rec ? RECOMMENDATION_LABEL[rec] : ''; },
        },
        { header: marksHeader.interviewer(r), width: 18, value: (a: AdmissionApplication) => sc(a)?.interviewer ?? '' },
        { header: marksHeader.comments(r), width: 30, value: (a: AdmissionApplication) => sc(a)?.comments ?? '' },
      ];
    }),
    ...(plan.custom?.criteria ?? []).map(c => ({
      header: marksHeader.custom(c), width: Math.max(14, c.label.length + 9),
      validation: { kind: 'decimal-range' as const, min: 0, max: c.maxMarks },
      value: (a: AdmissionApplication) => a.customScores?.[c.key] ?? null,
    })),
  ];
  const rows = markableApplications(apps, plan);
  return writeSheet({
    sheetName: 'Marks',
    columns: columns.map(({ header, width, validation }) => ({ header, width, validation })),
    rows: rows.map(a => columns.map(c => c.value(a))),
    emptyRowsForValidation: 0,
  });
}

// ─── Parse + check ──────────────────────────────────────────────────────────

const norm = (s: string) => s.trim().toLowerCase().replace(/\s+/g, ' ');
const show = (v: number | null | undefined) => (v == null ? '—' : String(v));

/** A mark cell: '' → not given, otherwise a number within 0…max, in the
 *  round's steps when it has them. */
function readMark(raw: string, max: number, label: string, errors: string[], step?: number): number | null {
  if (raw.trim() === '') return null;
  const n = Number(raw.trim());
  if (!Number.isFinite(n)) { errors.push(`${label}: "${raw}" is not a number.`); return null; }
  if (n < 0 || n > max) { errors.push(`${label} must be between 0 and ${max}.`); return null; }
  if (step && !onStep(n, step)) { errors.push(`${label}: marks go in steps of ${step}.`); return null; }
  return n;
}

/** Read a pathway's marks sheet. `apps` is the whole cycle, so a row for an
 *  applicant on another pathway is named as such rather than "not found". */
export async function parseMarksSheet(
  file: File,
  cycle: AdmissionCycle,
  pathway: AssessmentPathway,
  apps: AdmissionApplication[],
): Promise<ParsedMarksRow[]> {
  const plan = assessmentPlan(pathway);
  const sheet = await readFirstSheetAsRows(file);
  if (sheet.length === 0) throw new Error('The sheet is empty.');
  const col = new Map(sheet[0].map((h, i) => [norm(h), i]));
  if (!col.has(norm(marksHeader.applicationNumber))) throw new Error(`The sheet needs an "${marksHeader.applicationNumber}" column. Download the template and fill that in.`);
  const cell = (row: string[], header: string) => { const i = col.get(norm(header)); return i == null ? '' : (row[i] ?? '').trim(); };
  const has = (header: string) => col.has(norm(header));

  const byNumber = new Map(apps.map(a => [a.applicationNumber, a]));
  const seen = new Set<string>();
  const out: ParsedMarksRow[] = [];

  sheet.slice(1).forEach((row, i) => {
    if (row.every(c => !c?.trim())) return;
    const rowNumber = i + 2;
    const applicationNumber = cell(row, marksHeader.applicationNumber).toUpperCase();
    const errors: string[] = [];
    const changes: string[] = [];
    const app = applicationNumber ? byNumber.get(applicationNumber) : undefined;
    const name = app?.name ?? cell(row, 'Applicant');

    if (!applicationNumber) errors.push('The application number is missing.');
    else if (!app) errors.push(`No applicant with application number ${applicationNumber} in this cycle.`);
    else if (seen.has(applicationNumber)) errors.push(`${applicationNumber} appears more than once in the sheet.`);
    if (applicationNumber) seen.add(applicationNumber);
    const theirs = app && pathwayFor(cycle, app.classId);
    if (theirs && theirs.key !== pathway.key) errors.push(`${app!.name} is on ${theirs.name} — upload their marks with the ${theirs.name} sheet.`);
    if (!app || errors.length) { out.push({ rowNumber, applicationNumber, name, app, changes, errors, state: 'error' }); return; }

    const blockers = markingBlockers(app, plan);
    const payload: BulkMarksPayloadRow = { applicationId: app._id };

    // Test papers — only the papers whose mark changed.
    const testScores: NonNullable<BulkMarksPayloadRow['testScores']> = [];
    for (const p of plan.test?.papers ?? []) {
      const header = marksHeader.paper(p);
      if (!has(header)) continue;
      const raw = cell(row, header);
      if (!raw) continue;
      const absent = /^a(bsent)?$/i.test(raw);
      const obtained = absent ? 0 : readMark(raw, p.maxMarks, p.subject, errors);
      if (!absent && obtained == null) continue;
      const cur = app.testScores?.find(s => s.paperKey === p.key);
      if (cur && cur.absent === absent && (absent || cur.obtained === obtained)) continue;
      testScores.push({ paperKey: p.key, obtained: obtained ?? 0, absent });
      changes.push(`${p.subject} ${cur ? (cur.absent ? ABSENT : cur.obtained) : '—'} → ${absent ? ABSENT : obtained}`);
    }
    if (testScores.length) {
      if (blockers.test) errors.push(blockers.test);
      payload.testScores = testScores;
    }

    // Interview rounds — a round is sent whole when anything in it changed.
    const rounds: NonNullable<BulkMarksPayloadRow['interviewRounds']> = [];
    for (const r of plan.interview?.rounds ?? []) {
      const cur = app.interviewRounds?.find(x => x.roundKey === r.key);
      const step = r.markStep || 1;
      const scoredQs = r.rubric.filter(isScored);
      const markCells = scoredQs.map(c => ({ c, raw: cell(row, marksHeader.criterion(r, c)) }));
      const totalRaw = scoredQs.length ? '' : cell(row, marksHeader.round(r));
      const anyMark = scoredQs.length ? markCells.some(m => m.raw) : !!totalRaw;
      if (!anyMark) continue;

      const entry: NonNullable<BulkMarksPayloadRow['interviewRounds']>[number] = { roundKey: r.key };
      let changed = false;
      const missing = markCells.filter(m => !m.raw).map(m => m.c.criterion);
      if (missing.length) { errors.push(`${r.name}: fill every scored question (missing ${missing.join(', ')}).`); continue; }
      const before = (c: { criterion: string }) => cur?.rubricScores?.find(x => x.criterion === c.criterion);
      entry.rubricScores = r.rubric.map(c => {
        if (isScored(c)) {
          const raw = markCells.find(m => m.c === c)!.raw;
          return { criterion: c.criterion, obtained: readMark(raw, c.maxMarks, `${r.name} – ${c.criterion}`, errors, step) ?? 0 };
        }
        // A column left out of the sheet keeps the saved answer; a blank cell clears it.
        let answer = has(marksHeader.answer(r, c)) ? cell(row, marksHeader.answer(r, c)) : (before(c)?.answer ?? '');
        if (answer && c.kind === 'choice') {
          const match = (c.options || []).find(o => norm(o) === norm(answer));
          if (!match) errors.push(`${r.name} – ${c.criterion}: "${answer}" isn't an option. Use one of: ${(c.options || []).join(', ')}.`);
          answer = match ?? answer;
        }
        if (!answer && c.required) errors.push(`${r.name}: answer "${c.criterion}".`);
        return { criterion: c.criterion, answer: answer || undefined };
      });
      changed = entry.rubricScores.some(x => (x.obtained !== undefined
        ? before(x)?.obtained !== x.obtained
        : (before(x)?.answer ?? '') !== (x.answer ?? '')));
      if (!scoredQs.length) {
        const t = readMark(totalRaw, r.maxMarks, r.name, errors, step);
        if (t == null) continue;
        entry.totalMarks = t;
        changed = changed || cur?.totalMarks !== t;
      }

      // As for answers: only columns in the sheet change what's saved.
      const recRaw = has(marksHeader.recommendation(r)) ? cell(row, marksHeader.recommendation(r)) : (cur?.recommendation ?? '');
      if (recRaw) {
        const rec = RECOMMENDATION_BY_LABEL.get(recRaw.toLowerCase())
          ?? (Object.keys(RECOMMENDATION_LABEL).includes(recRaw) ? recRaw as InterviewRecommendation : undefined);
        if (!rec) errors.push(`${r.name}: unknown recommendation "${recRaw}". Use one of: ${Object.values(RECOMMENDATION_LABEL).join(', ')}.`);
        entry.recommendation = rec;
      }
      entry.interviewer = (has(marksHeader.interviewer(r)) ? cell(row, marksHeader.interviewer(r)) : cur?.interviewer) || undefined;
      entry.comments = (has(marksHeader.comments(r)) ? cell(row, marksHeader.comments(r)) : cur?.comments) || undefined;
      changed = changed
        || (entry.recommendation ?? '') !== (cur?.recommendation ?? '')
        || (entry.interviewer ?? '') !== (cur?.interviewer ?? '')
        || (entry.comments ?? '') !== (cur?.comments ?? '');
      if (!changed) continue;

      const total = scoredQs.length ? entry.rubricScores.reduce((t, x) => t + (x.obtained ?? 0), 0) : entry.totalMarks;
      changes.push(cur?.totalMarks === total ? `${r.name} notes` : `${r.name} ${show(cur?.totalMarks)} → ${total}`);
      rounds.push(entry);
    }
    if (rounds.length) {
      if (blockers.interview) errors.push(blockers.interview);
      payload.interviewRounds = rounds;
    }

    // Additional criteria — only the criteria whose mark changed.
    const custom: Record<string, number> = {};
    for (const c of plan.custom?.criteria ?? []) {
      const raw = cell(row, marksHeader.custom(c));
      if (!raw) continue;
      const v = readMark(raw, c.maxMarks, c.label, errors);
      if (v == null || app.customScores?.[c.key] === v) continue;
      custom[c.key] = v;
      changes.push(`${c.label} ${show(app.customScores?.[c.key])} → ${v}`);
    }
    if (Object.keys(custom).length) {
      if (blockers.custom) errors.push(blockers.custom);
      payload.customScores = custom;
    }

    const state = errors.length ? 'error' : changes.length ? 'ready' : 'unchanged';
    out.push({ rowNumber, applicationNumber, name, app, changes, errors: Array.from(new Set(errors)), state, payload: state === 'ready' ? payload : undefined });
  });
  return out;
}
